/**
 * P1 Issue #20 Fix: Centralized production-safe logging system
 * Replaces console statements with environment-aware logging
 */

export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3
}

type LogContext = Record<string, unknown>;

interface LogEntry {
  level: LogLevel;
  message: string;
  context?: LogContext;
  timestamp: number;
  component?: string;
}

class Logger {
  private logs: LogEntry[] = [];
  private maxLogs = 100;
  private isDevelopment = import.meta.env.NODE_ENV === 'development';
  private minLevel = this.isDevelopment ? LogLevel.DEBUG : LogLevel.WARN;

  private log(level: LogLevel, message: string, context?: LogContext, component?: string) {
    const entry: LogEntry = {
      level,
      message,
      context,
      timestamp: Date.now(),
      component
    };

    this.logs.push(entry);

    // Keep only recent logs
    if (this.logs.length > this.maxLogs) {
      this.logs.shift();
    }

    // Only console log in development or for important messages
    if (level >= this.minLevel) {
      const prefix = component ? `[${component}]` : '';
      const timestamp = new Date().toLocaleTimeString();
      
      switch (level) {
        case LogLevel.DEBUG:
          if (this.isDevelopment) {
            console.debug(`${timestamp} ${prefix} ${message}`, context || '');
          }
          break;
        case LogLevel.INFO:
          if (this.isDevelopment) {
            console.info(`${timestamp} ${prefix} ${message}`, context || '');
          }
          break;
        case LogLevel.WARN:
          console.warn(`${timestamp} ${prefix} ${message}`, context || '');
          break;
        case LogLevel.ERROR:
          console.error(`${timestamp} ${prefix} ${message}`, context || '');
          break;
      }
    }
  }

  debug(message: string, context?: LogContext, component?: string) {
    this.log(LogLevel.DEBUG, message, context, component);
  }

  info(message: string, context?: LogContext, component?: string) {
    this.log(LogLevel.INFO, message, context, component);
  }

  warn(message: string, context?: LogContext, component?: string) {
    this.log(LogLevel.WARN, message, context, component);
  }

  error(message: string, context?: LogContext, component?: string) {
    this.log(LogLevel.ERROR, message, context, component);
  }

  // Error boundary integration
  logError(error: Error, context?: LogContext, component?: string) {
    this.error(error.message, {
      stack: error.stack,
      ...context
    }, component);
  }

  // Get logs for debugging/support
  getLogs(level?: LogLevel): LogEntry[] {
    if (level !== undefined) {
      return this.logs.filter(log => log.level >= level);
    }
    return [...this.logs];
  }

  // Performance logging
  logPerformance(operation: string, duration: number, component?: string) {
    if (duration > 1000) { // Log slow operations
      this.warn(`Slow operation: ${operation} took ${duration}ms`, { duration }, component);
    } else if (this.isDevelopment) {
      this.debug(`${operation} completed in ${duration}ms`, { duration }, component);
    }
  }

  // User action logging (for analytics)
  logUserAction(action: string, context?: LogContext) {
    this.info(`User action: ${action}`, context, 'UserAnalytics');
  }

  clear() {
    this.logs = [];
  }
}

export const logger = new Logger();